import { Link } from "wouter";
import { INDEX_SYMBOL } from "../lib/data";

export default function DocsPage({ data }) {
  const schedule = data?.schedule || {};
  const payoutMin = schedule.payoutIntervalMin || 10;
  const basketMin = schedule.basketRefreshMin || data?.basket?.refreshMin || 60;
  const minSol = schedule.minDistributeSol ?? 0.02;

  return (
    <section className="app-section" style={{ paddingTop: "2.5rem" }}>
      <div className="shell readable docs">
        <p className="section-kicker">
          <i className="brand-diamond" aria-hidden /> Docs
        </p>
        <h2 className="section-title">How Stonksindex works</h2>
        <p className="section-lead" style={{ marginLeft: 0, textAlign: "left", maxWidth: "40rem" }}>
          {`$${INDEX_SYMBOL} is a rotating index of top StonkFun launches. Trading
          fees fund the board; holders receive weighted airdrops on a fixed
          payout clock.`}
        </p>

        <article className="docs-block">
          <h3>1. The token</h3>
          <p>
            {`$${INDEX_SYMBOL} is a StonkFun `}
            <strong>standard</strong> fee coin on the <strong>2%</strong> tier
            (~1.5% creator share of volume). Fees accrue on-chain and are
            claimable by the Stonksindex worker — not StonkFun auto reward-tax
            mode — so the protocol chooses what to buy and when holders are paid.
          </p>
          <p>
            Trade on StonkFun or Jupiter from the <Link href="/swap">Swap</Link>{" "}
            page. Always verify the contract address on{" "}
            <Link href="/token">Token</Link> before buying.
          </p>
        </article>

        <article className="docs-block">
          <h3>2. Scoring & basket</h3>
          <p>
            The worker reads StonkFun markets (market cap, 24h volume, liquidity,
            24h change), applies floors, scores the universe, and builds a
            weighted board of the top names (default size: 5). Weights follow
            relative scores so stronger names take a larger share of each spend.
          </p>
          <p>
            The <strong>basket refreshes every {basketMin} minutes</strong>.
            Enter/exit diffs are archived under{" "}
            <Link href="/history">History</Link>; the live board is on{" "}
            <Link href="/basket">Basket</Link>.
          </p>
        </article>

        <article className="docs-block">
          <h3>3. Payout timing</h3>
          <p>
            Spend attempts run on a <strong>fixed UTC clock every {payoutMin}{" "}
            minutes</strong> (:00, :{String(payoutMin).padStart(2, "0")}, …).
            The homepage countdown is aligned to that schedule in real time.
          </p>
          <p>
            A cycle only distributes when claimable creator fees (plus any
            spendable treasury buffer) meet the minimum — currently{" "}
            <strong>≥ {minSol} SOL</strong>. Holders need at least{" "}
            <strong>
              {(schedule.minHolderTokens ?? 10000).toLocaleString()} $
              {INDEX_SYMBOL}
            </strong>{" "}
            ({schedule.indexDecimals ?? 9} decimals) to receive airdrops.
            If the fee gate fails, the attempt is a no-op and the next window
            retries. Successful runs are archived on{" "}
            <Link href="/cycles">Cycles</Link>.
          </p>
          <ul>
            <li>
              <strong>Payout cadence:</strong> every {payoutMin} minutes (UTC)
            </li>
            <li>
              <strong>Basket refresh:</strong> every {basketMin} minutes
            </li>
            <li>
              <strong>Minimum to spend:</strong> {minSol} SOL claimable
            </li>
            <li>
              <strong>Minimum to earn:</strong>{" "}
              {(schedule.minHolderTokens ?? 10000).toLocaleString()} $
              {INDEX_SYMBOL}
            </li>
          </ul>
        </article>

        <article className="docs-block">
          <h3>4. Spend cycle</h3>
          <p>When a payout window clears the minimum, the worker:</p>
          <ol>
            <li>Claims StonkFun creator fees into the protocol wallet</li>
            <li>Reserves a SOL buffer for rent / ops</li>
            <li>Skims ~{schedule.lotteryPct ?? 10}% of the cycle into the holder lottery pot</li>
            <li>Swaps the remaining budget into basket weights (Jupiter → Raydium fallback → SOL)</li>
            <li>
              {`Airdrops tokens pro-rata to $${INDEX_SYMBOL} holders`}
            </li>
          </ol>
          <p>
            Closed or blacklisted ATAs receive SOL for that leg instead of the
            basket token. ATA rent creation is capped per cycle so airdrops stay
            solvent.
          </p>
        </article>

        <article className="docs-block">
          <h3>5. Lottery</h3>
          <p>
            About <strong>{schedule.lotteryPct ?? 10}%</strong> of each spend
            cycle accrues to a SOL pot. Draws fire on a{" "}
            <strong>
              random {schedule.lotteryIntervalMin ?? 20}–
              {schedule.lotteryIntervalMax ?? 60} minute
            </strong>{" "}
            cadence. Every eligible holder (≥{" "}
            {(schedule.minHolderTokens ?? 10000).toLocaleString()}{" "}
            {`$${INDEX_SYMBOL}`}) gets <strong>one ticket</strong> — bag size
            does not change odds. See <Link href="/lottery">Lottery</Link> for
            the live pot and past winners.
          </p>
        </article>

        <article className="docs-block">
          <h3>6. Portfolio & ATAs</h3>
          <p>
            Paste any wallet on <Link href="/portfolio">Portfolio</Link> to see
            live {`$${INDEX_SYMBOL}`} balance, supply share, on-chain holdings,
            and ATA troubleshooting (missing accounts, rent, open vs empty).
            Keep ATAs open if you want token airdrops rather than SOL fallbacks.
          </p>
        </article>

        <article className="docs-block">
          <h3>7. Risks</h3>
          <p>
            Memecoins and launchpad assets are volatile and illiquid. Payout size
            depends on trading volume, successful swaps, and the minimum fee
            gate. Cycles can skip when claimable is below threshold. This is not
            financial advice — see <Link href="/terms">Terms</Link> and{" "}
            <Link href="/privacy">Privacy</Link>. More Q&A in the{" "}
            <Link href="/faq">FAQ</Link>.
          </p>
        </article>
      </div>
    </section>
  );
}
